import { useTranslate } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Popover } from 'module/ui/components/atoms/popover';
import { Short01 } from 'module/ui/components/styles';
import { faChevronLeft, faPage } from '@fortawesome/pro-regular-svg-icons';
import { Button, InputField } from '@rocketlane/ui';
import { Form } from 'antd';
import { Document } from 'api/pages';
import { useUpdateDocumentTitleMutation } from 'queries/pages';
import { FC, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const Wrapper = styled(CenterAlignedBox)`
  ${Short01};
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 0;
`;
const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  padding: var(--spacing-02);
  width: 304px;
  .ant-form {
    flex: 1;
  }
`;
const TitleIcon = styled(FontAwesomeIcon)`
  color: var(--icon-secondary);
`;
const DocumentTitle = styled.div`
  cursor: pointer;
`;

interface DocumentTitleInputProps {
  value: string;
  onChange?: (value: string) => void;
  className?: string;
}
export const DocumentTitleInput: FC<DocumentTitleInputProps> = ({
  className,
  value,
  onChange,
}) => {
  const translate = useTranslate();
  const documentTitlePlaceholderText = translate('title.placeholder');
  const [form] = Form.useForm();
  const inputRef = useRef<HTMLInputElement>(null);
  const onAddOptionInputKeyDown = useCallback(
    (event) => {
      event.stopPropagation();
      if (event.key === 'Enter') {
        form
          .validateFields()
          .then(({ documentTitle }) => {
            onChange?.(documentTitle);
          })
          .catch(() => {});
      }
    },
    [form, onChange],
  );
  const onAddOptionInputBlur = useCallback(() => {
    form
      .validateFields()
      .then(({ documentTitle }) => {
        onChange?.(documentTitle);
      })
      .catch(() => {
        form.setFields([
          {
            name: 'documentTitle',
            errors: [],
          },
        ]);
      });
  }, [form, onChange]);

  useEffect(() => {
    form.setFieldsValue({
      documentTitle: value,
    });
  }, [form, value]);

  return (
    <InputWrapper>
      <TitleIcon icon={faPage} size={32} iconSize={16} color="transparent" />
      <Form
        layout="vertical"
        onSubmitCapture={(event) => {
          event.preventDefault();
        }}
        form={form}
        initialValues={{
          documentTitle: '',
        }}
        className={className}
      >
        <FormItem
          name={'documentTitle'}
          rules={[
            {
              required: true,
              message: (
                <FormattedMessage id="meeting_page_template.error.title_required" />
              ),
            },
          ]}
        >
          <InputField
            placeholder={documentTitlePlaceholderText}
            onKeyDown={onAddOptionInputKeyDown}
            onBlur={onAddOptionInputBlur}
            ref={inputRef}
          />
        </FormItem>
      </Form>
    </InputWrapper>
  );
};

interface Props {
  document: Document;
  onBack: () => void;
}

export const DocumentPageHeaderTitle = ({ document, onBack }: Props) => {
  const documentId = document.metadata.documentId;
  const { mutate: updateTitle } = useUpdateDocumentTitleMutation(documentId);

  const handleTitleChange = useCallback(
    (newTitle: string) => {
      if (newTitle.trim() !== document.metadata.title) {
        updateTitle(newTitle.trim());
      }
    },
    [document.metadata.title, updateTitle],
  );

  return (
    <Wrapper>
      <Button
        variant="secondary"
        leftIcon={<FontAwesomeIcon icon={faChevronLeft} size={12} />}
        onClick={onBack}
      />
      <Popover
        trigger="click"
        placement="bottomLeft"
        content={
          <DocumentTitleInput
            value={document.metadata.title}
            onChange={handleTitleChange}
          />
        }
      >
        <DocumentTitle>{document.metadata.title}</DocumentTitle>
      </Popover>
    </Wrapper>
  );
};
